import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import { usePWA } from '../hooks/usePWA';

export default function PWAInstallBanner() {
  const { isInstallable, isInstalled, isOnline, promptInstall } = usePWA();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('pwa-banner-dismissed') === 'true'
  );
  const [installing, setInstalling] = useState(false);
  const [isSamsungBrowser, setIsSamsungBrowser] = useState(false);

  useEffect(() => {
    // Detect if the user is browsing via Samsung Internet
    setIsSamsungBrowser(/SamsungBrowser/i.test(navigator.userAgent));
  }, []);

  const handleInstall = async () => {
    setInstalling(true);
    const accepted = await promptInstall();
    if (accepted) setDismissed(true);
    setInstalling(false);
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa-banner-dismissed', 'true');
  };

  // Offline banner — always show when offline
  if (!isOnline) {
    return (
      <div className="fixed top-0 left-0 w-full z-[9999] bg-orange-500 text-white px-4 py-2 flex items-center justify-center gap-2 text-xs font-black uppercase tracking-widest animate-in slide-in-from-top-2">
        <SafeIcon name="WifiOff" className="text-sm" />
        You are offline — some features may be limited
      </div>
    );
  }

  // Install banner
  if (!isInstallable || isInstalled || dismissed) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-[9998] max-w-md mx-auto animate-in slide-in-from-bottom-4 duration-500">
      <div className="bg-brand-dark text-white rounded-2xl p-4 shadow-2xl border border-white/10 flex items-center gap-3">
        <div className="w-12 h-12 bg-brand-primary rounded-xl flex items-center justify-center flex-shrink-0">
          <SafeIcon name="Download" className="text-white text-xl" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-black text-sm">Install BookOTG</p>
          <p className="text-[10px] text-gray-400 font-medium leading-tight">
            Add to your home screen for the best experience
          </p>
          
          {/* Helpful tip for Samsung Internet users regarding Play Protect */}
          {isSamsungBrowser && (
            <div className="mt-1.5 p-1.5 bg-orange-500/10 rounded-lg border border-orange-500/20">
              <p className="text-[9px] text-orange-400 font-bold leading-tight">
                <SafeIcon name="AlertTriangle" className="inline text-[9px] mr-1" />
                Samsung Users: If Play Protect blocks this, tap "More details" → "Install anyway" or use Chrome.
              </p>
            </div>
          )}
        </div>
        
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          <button
            onClick={handleInstall}
            disabled={installing}
            className="px-4 py-2 bg-brand-primary text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-60"
          >
            {installing ? 'Installing...' : 'Install'}
          </button>
          <button
            onClick={handleDismiss}
            className="px-4 py-1.5 text-gray-400 text-[10px] font-bold uppercase tracking-widest hover:text-white transition-colors text-center"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}